import {
  IProgramResponse,
  ProgramTypes,
} from '@carm/_core/access-control-mgt/models';
import {
  IBusinessAuthorizedOfficialApiResponse,
  IBusinessOwner,
  IBusinessOwnerApiResponse,
  IBusinessOwnership,
  IBusinessOfficial,
  IBusinessOwnershipResponse,
  IBusinessProfileResponse,
  IBusinessSettingsAndPreferences,
  IBusinessSettingsAndPreferencesResponse,
  IProgramInformation,
  IProgramListItem,
  IProgramProfileResponse,
  KeyValue,
  IBusinessContactsResponse,
  IBusinessContactResponse,
  IBusinessAddressResponse,
  IProgramGoodsAndLicensesResponse,
  IProgramPremisesInformationResponse,
  IBusinessOwnerPhoneApiResponse,
  IBusinessProfileContactInfoResponse,
  IProgramInformationSubmitRequest,
  ISubProgramActiveResponse,
} from '@carm/_core';
import {
  IBusinessProfileInformation,
  IBusinessProfileInformationContact,
  IBusinessProfileInformationLegalName,
  IBusinessAddress,
  ISubProgramActive,
  ISubProgramPending,
  IProgramDutiesRelief,
  IProgramOtherInformation,
} from '@carm/_core/program-account-profile/models/program-account-profile.model';
import { TranslateService } from '@ngx-translate/core';

import * as dayjs from 'dayjs';
import {
  IBaseAddressResponse,
  IBaseSocialMediaResponse,
} from '@carm/_core/access-control-mgt/models/address-api.model';
import {
  IBaseAddress,
  IBaseSocialMedia,
} from '@carm/shared/components/form-elements/models';
import { PhoneNumberMask, RegistrationProgramType } from '@carm/_core/business-registration/models';
import { NgxMaskPipe } from 'ngx-mask';
import { MetadataService } from '@carm/_core/metadata/services';
import { map, mergeMap, take } from 'rxjs/operators';
import { parsePhoneNumberToIntTelFormGroup } from '@carm/_utils/utility-functions';
import { Observable } from 'rxjs';
import { IHaveCarrierInformation, IProgramEnrolmentDraftResponse, IBaseContactResponse, IBaseContactSubmit, IProgramEdiServiceResponse } from '@carm/_core/program-enrolment/models/enrol-program-api.model';
import { CarrierSubProgramType, RppEnrolmentStatus, ProgramEnrolmentType, CaseTypes, WarehouseSubProgramType, ThirdPartyEdiServiceType, SubProgramEnrolmentType } from '@carm/_core/program-enrolment/models/enrol-program-enums.model';
import { SubProgramResponseKeys, ContactTypes } from '../models/program-account-enums.model';
import { IProgramContact } from '../models/program-contact.model';
import { IProgramEdiService } from '../models/program-services.model';
import { IContact } from '@carm/_core/business/models/contact.model';
import { RoutesConst } from '@carm/shared/constants/const-routes';

const skipMask = 'skipMask';

export const mapToProgramInformation = (
  from: IProgramProfileResponse,
  translateService: TranslateService,
  overrideNulls: boolean = false
): IProgramInformation => {
  const mappedObject = {
    programName: from?.programName,
    programAccountNumber: from?.programAccountNumber,
    programCategory: from?.programCategory,
    programType: from?.programType,
    importerType: from?.importerType,
    carrierCode: from?.carrierCode,
    carrierMode: from?.carrierMode,
    bondStatus: from?.bondedStatus === true,
    activeCase: from?.activeCase,
    hasActiveCase: !!from?.activeCase,
    licenseExpiryDate: from?.licenseExpiryDate,
    licenseNumber: from?.licenseNumber,
    otherInformation: mapToProgramOtherInformation(
      from?.carrier,
      translateService
    ),
    goodsNLicences: mapToProgramGoodsAndLicenses(
      from?.goodsNLicences,
      translateService
    ),
    premisesInfo: mapToProgramPremisesInformation(
      from?.premisesInfo,
      translateService
    ),
    warehouseType: from?.warehouseType,
    subLocationNumber: from?.subLocationNumber,
    serviceType: from?.serviceType,
    foreignEntityTypes: from?.foreignEntityTypes?.length > 0
        ? from?.foreignEntityTypes
        : undefined,
    subTypeOfWarehouse: from?.subTypeOfWarehouse,
    transactionControlNumber: from?.transactionControlNumber,
    owners: from?.owners,
    disbursments: from?.optIn,
    carrierCategory: mapS4CarrierSubProgram(from?.programCategory),
    isCommercialImporter: from.importerType === 'Commercial Importer' // backend
  };
  if (overrideNulls) {
    displayMessageIfDoesntExist(mappedObject, translateService);
  }
  return mappedObject as IProgramInformation;
};

export const mapToProgramOtherInformation = (
  from: IHaveCarrierInformation,
  translateService: TranslateService
) => {
  const mappedObject = {
    imoNumber: from?.imoIdNumber,
    numberOfVessels: from?.nbEquipment,
    numberOfEquipment: from?.nbEquipment,
    iataCode: from?.airIataCode,
    makeModelOfLargestAircraft: from?.makeModelLargestAircraft,
    numberOfLargeAircrafts: from?.nbLargePassAircrafts,
    numberOfLargeCargoAircrafts: from?.nbLargeCargoAircrafts,
    numberOfMediumAircrafts: from?.nbMediumPassAircrafts,
    numberOfSmallAircrafts: from?.nbSmallPassAircrafts,
    numberOfSmallOrMediumCargoAircrafts: from?.nbSmallMedCargoAircrafts,
    ownOperateLocomotive: from?.ownLocomotive,
  } as IProgramOtherInformation;
  displayMessageIfDoesntExist(mappedObject, translateService);
  return mappedObject;
};

export const mapToProgramGoodsAndLicenses = (
  from: IProgramGoodsAndLicensesResponse,
  translateService: TranslateService
) => {
  const mappedObject = {
    liquorExpiryDate: from?.liquorExpiryDate,
    cargoHandlingExpiryDate: from?.cargoHandlingExpiryDate,
    exciseExpiryDate: from?.exciseExpiryDate,
    firearmsExpiryDate: from?.firearmsExpiryDate,
    goodsExWarehouseFor: from?.goodsExWarehouseFor,
    goodsTypes: from?.goodsTypes,
  };
  displayMessageIfDoesntExist(mappedObject, translateService);
  return mappedObject;
};

export const mapToProgramPremisesInformation = (
  from: IProgramPremisesInformationResponse,
  translateService: TranslateService
) => {
  const mappedObject = {
    ediCapability: from?.ediCapability,
    goodsImportedBy: from?.goodsImportedBy,
    environment: from?.environment,
    operationValidity: from?.operationValidity ?? skipMask,
    ownership: from?.ownership,
    yearlyOrSeasonal: from?.yearlyOrSeasonal,
    leaseExpiryDate: from?.leaseExpiryDate,
    distance: from?.distance,
    canadianEntryPort: from?.canadianEntryPort,
    publicFacility: from?.publicFacility,
  };
  displayMessageIfDoesntExist(mappedObject, translateService);
  return mappedObject;
};

export const mapToProgramInformationSubmitRequest = (
  from: IProgramInformation
): IProgramInformationSubmitRequest => {
  const programType = from.programType;
  const payload: IProgramInformationSubmitRequest = {
    foreignEntityTypes: from?.foreignEntityTypes?.length > 0
    ? from?.foreignEntityTypes
    : undefined,
    optIn: from.disbursments,
    programName: from.programName,
  };

  if (
    programType === ProgramTypes.Carriers &&
    from.carrierMode !== CarrierSubProgramType.CarrierFreightForwarder
  ) {
    payload.carrier = {
      airCommercialPassengers: null,
      airIataCode: from?.otherInformation?.iataCode,
      carrierType: null,
      certifyByTcp: null,
      imoIdNumber: from?.otherInformation?.imoNumber,
      makeModelLargestAircraft:
        from?.otherInformation?.makeModelOfLargestAircraft,
      nbEquipment: from.carrierMode === CarrierSubProgramType.Highway
        ? from?.otherInformation?.numberOfEquipment
        : from?.otherInformation?.numberOfVessels,
      nbLargeCargoAircrafts:
        from?.otherInformation?.numberOfLargeCargoAircrafts,
      nbLargePassAircrafts: from?.otherInformation?.numberOfLargeAircrafts,
      nbMediumPassAircrafts: from?.otherInformation?.numberOfMediumAircrafts,
      nbSmallMedCargoAircrafts:
        from?.otherInformation?.numberOfSmallOrMediumCargoAircrafts,
      nbSmallPassAircrafts: from?.otherInformation?.numberOfSmallAircrafts,
      ownLocomotive: from?.otherInformation?.ownOperateLocomotive,
      requireSharedSecret: null,
      typeOfAircraft: null,
    };
  }
  return payload;
};

export const mapToBusinessInformation = (
  from: IBusinessProfileResponse,
  translateService: TranslateService
): IBusinessProfileInformation => {
  return {
    legalName: mapToLegalName(from, translateService),
    contact: mapToProfileInformationContact(from),
  } as IBusinessProfileInformation;
};

const mapToLegalName = (
  from: IBusinessProfileResponse,
  translateService: TranslateService
): IBusinessProfileInformationLegalName => {
  const mappedObject = {
    businessNumber: from?.businessNumber,
    legalEntityName: from?.legalEntityName,
    operatingName: from?.operatingName,
    operatingNames: from?.businessOperatingNames?.map(businessOperating => businessOperating?.operatingName),
    businessEffectiveDate: from.businessEffectiveDate ? dayjs(from.businessEffectiveDate).format('YYYY-MM-DD') : null,
  } as IBusinessProfileInformationLegalName;
  displayMessageIfDoesntExist(mappedObject, translateService);
  return mappedObject;
};

const mapToProfileInformationContact = (
  from: IBusinessProfileResponse
): IBusinessProfileInformationContact => {
  return {
    email: from?.contactInfo?.email,
    telIntFormGroup: parsePhoneNumberToIntTelFormGroup(from.contactInfo.phone),
    extension: from?.contactInfo?.extension,
    fax: from?.contactInfo?.fax,
    website: from?.contactInfo?.website,
  } as IBusinessProfileInformationContact;
};

export const mapToProfileInformationContactToSubmit = (
  from: IBusinessProfileInformationContact
): IBusinessProfileContactInfoResponse => {
  return {
    email: from?.email,
    phone: from?.telIntFormGroup.e164Phone,
    extension: from?.extension,
    fax: from?.fax,
    website: from?.website,
  } as IBusinessProfileContactInfoResponse;
};

const displayMessageIfDoesntExist = (
  object: any,
  translateService: TranslateService
) => {
  Object.keys(object).forEach((key: any) => {
    object[key] =
      object[key] !== undefined && object[key] !== ''
        ? object[key] === skipMask ? undefined : object[key]
        : translateService.instant('notifications.not_available');
  });
};

export const removeMessageIfDoesntExist = (
  object: any,
  translateService: TranslateService
) => {
  Object.keys(object).forEach((key: any) => {
    if (typeof object[key] === 'object' && object[key] !== null) {
      removeMessageIfDoesntExist(object[key], translateService);
    } else {
      isMessageEmpty(object, key, translateService);
    }
  });
};

const isMessageEmpty = (
  object: any,
  key: any,
  translateService: TranslateService
) => {
  object[key] =
    object[key] === translateService.instant('notifications.not_available')
      ? null
      : object[key];
};

export const mapToProgramListItem = (
  from: IProgramResponse,
  translator: TranslateService,
  metadataService: MetadataService
): IProgramListItem => {
  let typeName = null;
  mapToProperTypeName(translator, metadataService, from.type, from.programSubType).pipe(take(1))
  .subscribe(properTypeName => typeName = properTypeName);
  return {
    uid: from.uid,
    accountName: from.operatingName || from.name,
    type: from.type,
    typeName,
  } as IProgramListItem;
};

export const mapToProperTypeName = (translator: TranslateService, metadataService: MetadataService, programType: string, subProgramType: string): Observable<string> => {
  return metadataService.getProgramEnrolmentTypes()
  .pipe(
    mergeMap((programTypes) => {
      const type = programTypes?.find(p => p.key === programType)?.value;
      const typeName = type ?? translator.instant(`program_account.profile.program_types.${programType}`);
      return metadataService.getProgramSubTypes()
      .pipe(
        map(foundSubType => {
        const subType = foundSubType?.find(st => st.key === subProgramType)?.value;
        const subTypeName = subType ?? typeName;
        return subTypeName;
      }));
    }));
}

export const mapToBaseAddress = (
  from: IBaseAddressResponse,
  countries: KeyValue[],
  regions: KeyValue[],
  addressTypes: KeyValue[],
  maskPipe: NgxMaskPipe,
): IBaseAddress => {
  let country =
    countries.find(ct => ct.key === from.country)?.value || from.country;

  const region =
    regions.find(rg => rg.key === from.region && rg.parentKey === from.country)
      ?.value || from.region;

  if (region) {
    country = commaFormat(country);
  }

  const fullAddress = `${from.careOf} |
  ${from.poBox} | ${from.line1} | ${from.line2} | ${from.town} |
  ${region}${country} | ${from.postalCode} | ${from.email}`;

  return {
    addressIdByExternalSystem: from.addressIdByExternalSystem,
    addressId: from.addressId,
    activeCase: from?.activeCase,
    careOf: from?.careOf,
    addressType: from.addressType,
    addressTypeName: addressTypes.find(type => type?.key === from?.addressType)?.value,
    line1: from?.line1,
    line2: from?.line2,
    fullAddress,
    town: from?.town,
    region: from?.region,
    country: from?.country,
    email: from?.email,
    postalCode: from?.postalCode,
    phone: from.phoneExt ? maskPipe.transform(from.phone, PhoneNumberMask.WorldWide) + ' #' + from.phoneExt
                         : maskPipe.transform(from.phone, PhoneNumberMask.WorldWide),
    faxNumber: from?.faxNumber,
    phoneExt: from?.phoneExt,
    languagePreference: from?.languagePreference,
  };
};

export const mapToSocialMedia = (
  from: IBaseSocialMediaResponse,
  socialMediaTypes: KeyValue[] = null
): IBaseSocialMedia => {
  const socialMediaType =
    from.socialMediaType && socialMediaTypes
      ? socialMediaTypes.find(ct => ct.key === from.socialMediaType)?.value
      : '';

  return {
    addressId: from.addressId,
    addressType: from.addressType,
    socialMediaType: from.socialMediaType,
    socialMediaTypeDisplay: socialMediaType,
    otherSocialMediaType: from.otherSocialMediaType,
    socialMediaAddress: from.socialMediaAddress,
  } as IBaseSocialMedia;
};

const commaFormat = (input: string): string => {
  return input ? ', ' + input : '';
};

export const mapToBusinessOwnership = (
  from: IBusinessOwnershipResponse,
  translateService: TranslateService
): IBusinessOwnership => {
  return {
    owners: from.ownerDataWsDto.map(owner =>
      mapToOwner(owner, translateService)
    ),
  } as IBusinessOwnership;
};

export const mapToOwner = (
  from: IBusinessOwnerApiResponse,
  translateService: TranslateService
): IBusinessOwner => {
  return {
    addressId: from.addressId,
    firstName: from.firstName,
    lastName: from.lastName,
    position: from.position,
    email: from.email,
    phones: from.phones,
    phoneType: mapToContactInfo(from.phones, 'phoneType'),
    phoneNumber: mapToContactInfo(from.phones, 'phoneNumber'),
    deviceType: mapToContactInfo(from.phones, 'deviceType'),
    languagePref: translateService.instant(`language.${from.language}`),
  } as IBusinessOwner;
};

export const mapOwnerToContact = (
  from: IBusinessOwnerApiResponse
): IContact => {
  return {
    contactTitle: from.position,
    firstName: from.firstName,
    lastName: from.lastName,
    email: from.email,
    telephone: from.phones[0].phoneNumber,
    languagePref: from.language,
    position: from.position,
  } as IContact;
};

export const mapToContactInfo = (
  ownerPhones: IBusinessOwnerPhoneApiResponse[],
  property: string
): string => {
  let contactInfo = '';
  ownerPhones.map(ownerPhone => {
    switch (property) {
      case 'phoneType':
        contactInfo += `${ownerPhone.phoneType} |`;
        break;
      case 'phoneNumber':
        contactInfo += `${ownerPhone.phoneNumber} ext. ${ownerPhone.phoneExtension} |`;
        break;
      case 'deviceType':
        contactInfo += `${ownerPhone.deviceType} |`;
        break;
    }
  });
  return contactInfo;
};

export const mapToAuthorizedOfficial = (
  from: IBusinessContactResponse
): IBusinessOfficial => {
  return {
    type: from.type,
    position: from.title,
    email: from.email,
    name: from.firstName + ' ' + from.lastName,
    firstName: from.firstName,
    lastName: from.lastName,
    fax: from.fax,
    languagePref: from.language,
    telephone: from.telephone,
  } as IBusinessOfficial;
};

export const mapToBusinessSettingsAndPreferences = (
  from: IBusinessSettingsAndPreferencesResponse,
  translateService: TranslateService,
  offSettingPreferences: KeyValue[]
): IBusinessSettingsAndPreferences => {
  const plannedUpdatePreference =
    offSettingPreferences.find(ct => ct.key === from.plannedOffsetting)
      ?.value || translateService.instant('notifications.not_available');

  const updateEffectiveDate = from.plannedOffsettingDate
    ? dayjs(from.plannedOffsettingDate).format('YYYY-MM-DD')
    : translateService.instant('notifications.not_available');

  return {
    language: from?.languagePreference,
    correspondence: from?.correspondencePreference,
    offsetting: from?.offsettingPreference,
    settingId: from.settingId,
    plannedUpdatePreference,
    hasPlannedUpdate: from.plannedOffsetting ? true : false,
    updateEffectiveDate,
    inCollection: from.inCollections !== '',
    canUpdateOffsettingPreferences: from.plannedOffsetting === '' && from.plannedOffsettingDate === ''
  } as IBusinessSettingsAndPreferences;
};

export const mapToBusinessSettingsAndPreferencesSubmit = (
  from: IBusinessSettingsAndPreferences
): IBusinessSettingsAndPreferencesResponse => {
  return {
    languagePreference: from.language,
    correspondencePreference: from.correspondence,
    offsettingPreference: from.offsetting,
    settingId: from.settingId,
  } as IBusinessSettingsAndPreferencesResponse;
};


export const mapToContacts = (from: IBusinessContactsResponse): IContact[] => {
  return from.contacts?.map(contact => mapToContact(contact));
};

export const mapToContact = (from: IBusinessContactResponse): IContact => {
  return {
    type: from.type,
    contactId: from.contactId,
    contactType: from.contactType,
    otherContactType: from.otherContactType,
    contactTitle: from.title,
    firstName: from.firstName,
    lastName: from.lastName,
    fullname: `${from.firstName} ${from.lastName}`,
    middleName: from.middleName,
    email: from.email,
    telIntFormGroup: parsePhoneNumberToIntTelFormGroup(from.telephone),
    telephone: from.telephone,
    fax: from.fax,
    languagePref: from.language,
    extension: from.extension,
  } as IContact;
};

export const mapToBusinessOfficial = (
  from: IBusinessAuthorizedOfficialApiResponse
): IContact => {
  return {
    contactId: from.uid,
    contactType: from.type,
    contactTitle: from.title,
    firstName: from.firstName,
    lastName: from.lastName,
    email: from.email,
    telephone: from.telephone,
    languagePref: from.language,
    position: from.position,
  } as IContact;
};

export const mapToBusinessAddress = (
  from: IBusinessAddressResponse
): IBusinessAddress => {
  return {
    type: from.type,
    addressType: from.addressType,
    addressIdByExternalSystem: from.addressIdByExternalSystem,
    uid: from.addressId,
    careOf: from.careOf,
    carmCountry: from.country,
    line1: from.streetName,
    line2: from.streetPrefixName,
    poBox: from.poBox,
    postalCode: from.postalCode,
    carmRegion: from.region,
    town: from.cityName,
    phone: from.phoneNumber,
  };
};

export const mapToActiveSubPrograms = (
  from: ISubProgramActiveResponse,
  enrolmentTypes: KeyValue[]
): ISubProgramActive[] => {
  const subProgramActive: ISubProgramActive[] = [];
  for (const property of Object.keys(SubProgramResponseKeys)) {
    const enrolmentType =
      enrolmentTypes.find(ct => ct.key === SubProgramResponseKeys[property])
        ?.value || SubProgramResponseKeys[property];
    if (!!from && (from[property] === true ||
        property === 'rppStatus' && from[property] === RppEnrolmentStatus.Enrolled)) {
      subProgramActive.push({
        type: SubProgramResponseKeys[property],
        enrolmentType,
      });
    }
  }
  return subProgramActive;
};

export const mapToPendingSubPrograms = (
  from: IProgramEnrolmentDraftResponse,
  translateService: TranslateService
): ISubProgramPending => {
  return {
    uid: from.draftId,
    subTypeName: translateService.instant(
      `sub-program-enrolment.requirements.sub-program-title.${from.subType}`
    ),
    subType: from.subType,
    enrolmentType: from.enrolmentType,
    caseNumber: from.caseNumber.toString(),
    status: from.statusCode,
    statusDisplay: from.statusMsg,
  } as ISubProgramPending;
};

export function mapBaseContactsResponse(
  from: IBaseContactResponse,
  translateService: TranslateService,
  ediServiceTypes: KeyValue[] = null,
  contactTitles: KeyValue[] = null,
  maskPipe: NgxMaskPipe,
  programContactTypes: KeyValue[] = null
): IContact {
  const ediServiceType =
    from.ediServiceType && ediServiceTypes
      ? ediServiceTypes.find(ct => ct.key === from.ediServiceType)?.value
      : '';

  const contactTitle =
    from.title && contactTitles
      ? contactTitles.find(ct => ct.key === from.title)?.value
      : '';

  const telephoneDisplay = from.extension
    ? `${maskPipe.transform(from.telephone, PhoneNumberMask.WorldWide)} Ext. ${from.extension}`
    : maskPipe.transform(from.telephone, PhoneNumberMask.WorldWide);

  const programContactType =
    from.contactType && programContactTypes
      ? programContactTypes.find(ct => ct.key === from.contactType)?.value
      : from.contactType;

  return {
    contactId: from.contactId,
    contactType: from.contactType,
    otherContactType: from.otherContactType,
    contactTypeDisplay: from.contactType === 'Other' ? (from.otherContactType || programContactType) : programContactType,
    contactTitle: from.title,
    otherContactTitle: from.otherContactTitle,
    contactTitleDisplay: from.title === ContactTypes.Other ? (from.otherContactTitle || from.title) : contactTitle,
    fullName: from.firstName + ' ' + from.lastName,
    firstName: from.firstName,
    lastName: from.lastName,
    middleName: from.middleName,
    email: from.email,
    telephone: from.telephone,
    telIntFormGroup: parsePhoneNumberToIntTelFormGroup(from.telephone),
    telephoneDisplay,
    fax: from.fax,
    faxDisplay: from.fax
      ? from.fax
      : translateService.instant(
          'program_account.profile.details.contacts.not_provided'
        ),
    languagePref: from.language,
    languagePrefDesplay: from.language
      ? translateService.instant(`language.${from.language}`)
      : translateService.instant(
          'program_account.profile.details.contacts.not_provided'
        ),
    extension: from.extension,
    ediServiceType: from.ediServiceType,
    ediServiceTypeName: ediServiceType,
  } as IProgramContact;
}

export function mapProgramTypeToProgramEnrolmentType(
  programType: ProgramTypes
): ProgramEnrolmentType {
  switch (programType) {
    case ProgramTypes.Importer:
    case ProgramTypes.ImporterR1:
      return ProgramEnrolmentType.Importer;
    case ProgramTypes.CustomsBroker:
      return ProgramEnrolmentType.CustomsBroker;
    case ProgramTypes.Carriers:
      return ProgramEnrolmentType.CarrierFreightForwarder;
    case ProgramTypes.Warehouse:
      return ProgramEnrolmentType.Warehouse;
    case ProgramTypes.DutyFreeShop:
      return ProgramEnrolmentType.DutyFreeShop;
    case ProgramTypes.Exporter:
      return ProgramEnrolmentType.Exporter;
    case ProgramTypes.ThirdPartyServiceProviderEDI:
      return ProgramEnrolmentType.ThirdParty;
    case ProgramTypes.ThirdPartyServiceProvider:
      return ProgramEnrolmentType.ThirdParty;
  }
}

const mapProgramEnrolmentStepsPages = new Map<string, string[]>([
    [`${ProgramEnrolmentType.Importer}_${RoutesConst.ProgramEnrolment_Details_Rel}`, [RoutesConst.ProgramEnrolment_Importer, RoutesConst.ProgramEnrolment_Details_Rel]],
    [`${ProgramEnrolmentType.Exporter}_${RoutesConst.ProgramEnrolment_Details_Rel}`, [RoutesConst.ProgramEnrolment_Exporter, RoutesConst.ProgramEnrolment_Details_Rel]],
    [`${ProgramEnrolmentType.ThirdParty}_${RoutesConst.ProgramEnrolment_Details_Rel}`, [RoutesConst.ProgramEnrolment_Thirdparty, RoutesConst.ProgramEnrolment_Details_Rel]],
    [`${ProgramEnrolmentType.CarrierFreightForwarder}_${RoutesConst.ProgramEnrolment_Details_Rel}`, [RoutesConst.ProgramEnrolment_Carrier, RoutesConst.ProgramEnrolment_Details_Rel]],
    [`${ProgramEnrolmentType.CustomsBroker}_${RoutesConst.ProgramEnrolment_Details_Rel}`, [RoutesConst.ProgramEnrolment_UploadEnrolment]],
    [`${ProgramEnrolmentType.DutyFreeShop}_${RoutesConst.ProgramEnrolment_Details_Rel}`, [RoutesConst.ProgramEnrolment_UploadEnrolment]],
    [`${ProgramEnrolmentType.Warehouse}_${RoutesConst.ProgramEnrolment_Details_Rel}`, [RoutesConst.ProgramEnrolment_UploadEnrolment]],
  ]);

export const getEnrolmentUrlFrom = (draftId: string, draftProgramType: ProgramEnrolmentType): string[] => {
  let detailsPageRoute = mapProgramEnrolmentStepsPages.get(`${draftProgramType}_${RoutesConst.ProgramEnrolment_Details_Rel}`);
  if (detailsPageRoute.length === 1) {
    return [detailsPageRoute[0], draftId];
  }
  return [detailsPageRoute[0], draftId, detailsPageRoute[1]];
};

export const mapCaseTypeToProgramTypes = new Map<
  CaseTypes,
  ProgramEnrolmentType
>([
  [CaseTypes.ZIMP, ProgramEnrolmentType.Importer],
  [CaseTypes.ZBRO, ProgramEnrolmentType.CustomsBroker],
  [CaseTypes.ZCAR, ProgramEnrolmentType.CarrierFreightForwarder],
  [CaseTypes.ZWHB, ProgramEnrolmentType.Warehouse],
  [CaseTypes.ZWHS, ProgramEnrolmentType.Warehouse],
  [CaseTypes.ZEXP, ProgramEnrolmentType.Exporter],
  [CaseTypes.ZDFS, ProgramEnrolmentType.DutyFreeShop],
  [CaseTypes.Z3RD, ProgramEnrolmentType.ThirdParty],
  [CaseTypes.ZEDI, ProgramEnrolmentType.ThirdParty],
]);

export const mapProgramEnrolmentTypeToProgramTypes = new Map<
  ProgramEnrolmentType,
  ProgramTypes
>([
  [ProgramEnrolmentType.Importer, ProgramTypes.ImporterR1],
  [ProgramEnrolmentType.CustomsBroker, ProgramTypes.CustomsBroker],
  [ProgramEnrolmentType.CarrierFreightForwarder, ProgramTypes.Carriers],
  [ProgramEnrolmentType.Warehouse, ProgramTypes.Warehouse],
  [ProgramEnrolmentType.DutyFreeShop, ProgramTypes.DutyFreeShop],
  [ProgramEnrolmentType.Exporter, ProgramTypes.Exporter],
  [ProgramEnrolmentType.ThirdParty, ProgramTypes.ThirdPartyServiceProvider],
]);

export const mapRegistrationProgramTypeToProgramEnrolmentType = new Map<
  RegistrationProgramType,
  ProgramEnrolmentType
>([
  [
    RegistrationProgramType.Carrier,
    ProgramEnrolmentType.CarrierFreightForwarder,
  ],
  [RegistrationProgramType.SufferanceWarehouse, ProgramEnrolmentType.Warehouse],
  [RegistrationProgramType.BondedWarehouse, ProgramEnrolmentType.Warehouse],
  [RegistrationProgramType.DutyFreeShop, ProgramEnrolmentType.DutyFreeShop],
]);

export function mapS4CarrierSubProgram(typeS4: string): CarrierSubProgramType {
  switch (typeS4) {
    case 'Air':
      return CarrierSubProgramType.Air;
    case 'Freight Forwarder':
      return CarrierSubProgramType.CarrierFreightForwarder;
    case 'Marine':
      return CarrierSubProgramType.Marine;
    case 'Rail':
      return CarrierSubProgramType.Rail;
    case 'Highway':
      return CarrierSubProgramType.Highway;
  }
}

export function mapS4DFS(typeS4: string): WarehouseSubProgramType {
  switch (typeS4) {
    case 'bw':
      return WarehouseSubProgramType.CustomsBondedWarehouse;
    case 'sw':
      return WarehouseSubProgramType.SufferanceWarehouse;
  }
}

export function mapToBaseContactSubmit(from: IContact): IBaseContactSubmit {
  return {
    contactId: from.contactId,
    contactType: from.contactType,
    email: from.email,
    extension: from.extension,
    fax: from.fax,
    firstName: from.firstName,
    language: from.languagePref,
    lastName: from.lastName,
    middleName: from.middleName,
    otherContactType: from.otherContactType,
    telephone: from.telIntFormGroup.e164Phone,
    title: from.contactTitle,
    otherContactTitle: from.otherContactTitle,
    ediServiceType: from.ediServiceType,
  };
}

export function mapProgramEdiServiceResponse(
  from: IProgramEdiServiceResponse,
  ediProgramTypes: KeyValue[] = null,
  ediConnectionMethods: KeyValue[] = null
): IProgramEdiService {
  const serviceName = from?.serviceName && ediProgramTypes
      ? ediProgramTypes.find(ct => ct.key === from.serviceName)?.value
      : '';
  const connectMethod = from?.connectMethod && ediConnectionMethods
      ? ediConnectionMethods.find(ct => ct.key === from.connectMethod)?.value
      : '';
  const serviceProvider = from?.serviceProvider;
  return {
    serviceName,
    connectMethod,
    serviceProvider,
  } as IProgramEdiService;
}

export function mapProgramDutiesReliefResponse(
  from: ISubProgramActiveResponse,
  translateService: TranslateService
): IProgramDutiesRelief {
  return {
    licenceNumber: from.dutyReliefLicenseNumber
      ? from.dutyReliefLicenseNumber
      : translateService.instant('notifications.not_available'),
    expiryDateOfLicence: from.dutyReliefLicenseExpiryDate
      ? from.dutyReliefLicenseExpiryDate
      : translateService.instant('notifications.not_available'),
  } as IProgramDutiesRelief;
}

export function mapServiceTypes(serviceType: string) {
  switch (serviceType) {
    case 'ACI':
      return ThirdPartyEdiServiceType.AdvanceCommercialInformation;
    case 'ACROSS':
      return ThirdPartyEdiServiceType.AcceleratedCommercialReleaseOperations;
    case 'CAD':
      return ThirdPartyEdiServiceType.CommercialAccountingDeclaration;
    case 'DN&SOA':
      return ThirdPartyEdiServiceType.DailyNoticeAndStatementOfAccount;
    case 'G7':
      return ThirdPartyEdiServiceType.G7Edi;
    case 'MER':
      return ThirdPartyEdiServiceType.MarineExportReporting;
    case 'RNS':
      return ThirdPartyEdiServiceType.ReleaseNotificationSystem;
    case 'SWI':
      return ThirdPartyEdiServiceType.IntegratedImportDeclaration;
  }
}

const enrolmentLogicMatrix = new Map<string, boolean>([
  [`${ProgramTypes.ImporterR1}_`, true],
  [`${ProgramTypes.ImporterR1}_${SubProgramEnrolmentType.ReleasePriorPayment}`, false],
  [`${ProgramTypes.ImporterR1}_${SubProgramEnrolmentType.TrustedTrader}`, false],
  [`${ProgramTypes.ImporterR1}_${SubProgramEnrolmentType.DutyReliefLicence}`, false],
  [`${ProgramTypes.ImporterR1}_${SubProgramEnrolmentType.ContainerBank}`, false],
  [`${ProgramTypes.ImporterR1}_${SubProgramEnrolmentType.CandianGoodsAbroad}`, false],
  [`${ProgramTypes.ImporterR1}_${SubProgramEnrolmentType.Edi}`, true],
  [`${ProgramTypes.Exporter}_`, true],
  [`${ProgramTypes.Exporter}_${SubProgramEnrolmentType.Edi}`, true],
  [`${ProgramTypes.DutyFreeShop}_`, true],
  [`${ProgramTypes.Carriers}_`, true],
  [`${ProgramTypes.Carriers}_${SubProgramEnrolmentType.TrustedTrader}`, false],
  [`${ProgramTypes.Carriers}_${SubProgramEnrolmentType.ReportingSummaryAccounting}`, false],
  [`${ProgramTypes.Carriers}_${SubProgramEnrolmentType.Edi}`, true],
  [`${ProgramTypes.Carriers}_${SubProgramEnrolmentType.Bonded}`, false],
  [`${ProgramTypes.Warehouse}_`, true],
  [`${ProgramTypes.Warehouse}_${SubProgramEnrolmentType.Edi}`, true],
  [`${ProgramTypes.ThirdPartyServiceProvider}_`, false],
  [`${ProgramTypes.ThirdPartyServiceProviderAcountant}_`, false],
  [`${ProgramTypes.ThirdPartyServiceProviderConsultant}_`, false],
  [`${ProgramTypes.ThirdPartyServiceProviderEDI}_`, true],
  [`${ProgramTypes.ThirdPartyServiceProviderLawyer}_`, false],
  [`${ProgramTypes.ThirdPartyServiceProviderSurety}_`, false],

]);

export function canEnrolOrUpdate(inProgressEnrollments: string[], enrolledSubPrograms: string[], programType: ProgramTypes, subprogram?: SubProgramEnrolmentType): boolean {
  const mappedSubProgram = subprogram || '';
  const key = `${programType}_${mappedSubProgram}`;
  const canEnrol = (subprogram === SubProgramEnrolmentType.ReleasePriorPayment &&
                      ![...inProgressEnrollments, ...enrolledSubPrograms].distinctList().includes(subprogram) ||
                    subprogram !== SubProgramEnrolmentType.ReleasePriorPayment &&
                      !inProgressEnrollments?.includes(subprogram)) ||
                    enrolmentLogicMatrix.get(key);
  return canEnrol;
}
